import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import DOMPurify from "dompurify";
import { useSelector, useDispatch } from "react-redux";
import {
	CustomIconButton,
	icon,
	EmptyComponent,
	CustomButton,
	CustomTooltip,
	IconComponent,
} from "../../../../../../common/components";
import { hasCompanyPermission, hasPermission, translate, Permissions } from "../../../../../../common/providers";
import styles from "./SectionDetails.module.css";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { useApi } from "../../../../../../common/hooks";
import { AiDetectionsService } from "../../../../../../api";
import DetectedContentRow from "./DetectedContentRow";
import { AssociateContentDialog } from "./AssociateContentDialog";
import { UpdateValueDialog } from "./UpdateValueDialog";
import { COMPLIANCE_STATUS, FIELD_STATUS, FIELD_TYPES, LINE_HEIGHT } from "../../constants";
import { displayValue, maxCollapseHeight } from "../../utils";
import UpdateComplianceDialog from "./UpdateComplianceDialog";
import { updateCounters } from "../../../slice/project-sheet-slice";

const LINES = 10;
const sortSources = (sources) => {
	const getSortValue = (val) => {
		if (val === true) {
			return 0;
		}
		if (val === null) {
			return 1;
		}
		return 2;
	};
	return [...sources].sort((a, b) => getSortValue(a.isVerified) - getSortValue(b.isVerified));
};
export const SectionDetails = ({
	criteria,
	launchInProgress,
	selectedCriteria,
	selectedPageId,
	onClose,
	onRefreshSections,
	onUpdateSelectedCriteria,
	onValidate,
	onUpdateCompliance,
}) => {
	const [refreshCounter, setRefreshCounter] = useState(0);
	const [criteriaInformation, setCriteriaInformation] = useState(null);
	const [openUpdateValue, setOpenUpdateValue] = useState(false);
	const [openDetectedContent, setOpenDetectedContent] = useState(true);
	const [openAssociateContent, setOpenAssociateContent] = useState(false);
	const [openComplianceDialog, setOpenComplianceDialog] = useState(false);
	const [selectedValue, setSelectedValue] = useState(null);
	const [sources, setSources] = useState([]);
	const [expanded, setExpanded] = useState(false);
	const [showButton, setShowButton] = useState(false);
	const [focusId, setFocusId] = useState(null);
	const [complianceStatus, setComplianceStatus] = useState(null);
	const [complianceComment, setComplianceComment] = useState("");
	const projectId = useSelector(({ context }) => context.project?.id);
	const itemRefs = useRef({});
	const { call: getFieldData } = useApi(AiDetectionsService.getFieldData);
	const containerRef = useRef(null);
	const dispatch = useDispatch();
	const hasRights = useMemo(
		() =>
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
			hasCompanyPermission([Permissions.PROJECT_LEADER]),
		[]
	);
	const criteriaValues = useMemo(() => criteria?.values ?? [], [criteria]);
	const checked = useMemo(
		() => isNonEmptyArray(criteriaValues) && criteriaValues[0].status === FIELD_STATUS.VALIDATED,
		[criteriaValues]
	);
	const validationTooltip = useMemo(() => {
		if (criteriaInformation?.values?.length > 0) {
			if (hasRights && !checked) {
				return "";
			}
			if (!hasRights && !checked) {
				return translate("dashboards.project-sheet.button.tooltip.inactive-not-validated");
			}
			if (hasRights && checked) {
				return translate("dashboards.project-sheet.button.tooltip.active-validated");
			}
			return translate("dashboards.project-sheet.button.tooltip.inactive-validated");
		}
		return "";
	}, [checked, hasRights, criteriaInformation]);
	const content = useMemo(
		() => ({
			value:
				isNonEmptyArray(criteriaInformation?.values) &&
				(criteriaInformation.values?.length === 1
					? displayValue(criteriaInformation.values[0].value, criteriaInformation?.fieldType)
					: criteriaInformation.values
							.map((val) => displayValue(val.value, criteriaInformation?.fieldType))
							.join("<br/>- ")),
			valueUuid: criteriaInformation?.values[0]?.valueUuid,
		}),
		[criteriaInformation]
	);
	useEffect(() => {
		const currentSources = [];
		if (isNonEmptyArray(criteriaInformation?.sources)) {
			criteriaInformation.sources.forEach((source) => {
				currentSources.push({
					...source,
					fieldUuid: criteriaInformation?.fieldUuid,
				});
			});
		}
		setSources(sortSources(currentSources));
	}, [criteriaInformation, selectedCriteria?.id]);
	useEffect(() => {
		if (projectId && selectedCriteria?.id) {
			setExpanded(false);
			getFieldData({ projectId, fieldUuid: selectedCriteria.id })
				.then((data) => {
					setCriteriaInformation(data);
				})
				.catch(console.error);
		}
	}, [getFieldData, selectedCriteria, projectId, refreshCounter]);
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}
		const fullHeight = containerRef.current.scrollHeight;
		setShowButton(fullHeight > maxCollapseHeight(LINES));
	}, [criteriaInformation]);
	useEffect(() => {
		if (focusId && itemRefs.current[focusId]) {
			itemRefs.current[focusId].focus();
		}
	}, [sources, focusId]);
	useEffect(() => {
		if (criteria?.innerFields) {
			const status = criteria.innerFields.find((inner) => inner.fieldName === "complianceStatus");
			setComplianceStatus(status?.value?.value?.toUpperCase() || null);
			const comment = criteria.innerFields.find((inner) => inner.fieldName === "complianceComment");
			setComplianceComment(comment?.value?.value || "");
		} else {
			setComplianceStatus(null);
			setComplianceComment("");
		}
	}, [criteria]);
	const handleOpenDetectedContent = () => {
		setOpenDetectedContent((prev) => !prev);
	};
	const handleLikeSource = (source, newStatus) => {
		if (source?.informationId) {
			const index = sources.findIndex((item) => item.informationId === source.informationId);
			const nextItem = sources[index + 1];
			if (nextItem) {
				setFocusId(nextItem.informationId);
			}
			setSources((prev) =>
				prev.map(
					(updatedSource) =>
						(updatedSource.informationId === source.informationId && {
							...updatedSource,
							isVerified: newStatus,
						}) ||
						updatedSource
				)
			);
		}
	};
	const handleOpenAssociateContent = () => {
		setOpenAssociateContent((prev) => !prev);
	};
	const handleOpenUpdateValueDialog = (value) => {
		setOpenUpdateValue((prev) => !prev);
		const { fieldType } = criteriaInformation || {};
		setSelectedValue(
			([FIELD_TYPES.LIST, FIELD_TYPES.CATEGORY].some((field) => field === fieldType) &&
				(criteriaInformation?.values || [])) ||
				value ||
				null
		);
	};
	const handleOpenUpdateComplianceDialog = () => {
		setOpenComplianceDialog((prev) => !prev);
	};
	const handleSubmit = () => {
		onRefreshSections();
		setRefreshCounter((prev) => prev + 1);
	};
	const handleUpdate = () => {
		handleSubmit();
		onUpdateSelectedCriteria({ ...selectedCriteria, isManuallyAdded: true });
	};
	const handleShowMore = () => {
		setExpanded((e) => !e);
	};
	const handleCheckValue = () => {
		onValidate(criteriaInformation?.fieldUuid);
	};
	const handleCheckResult = useCallback(
		(item) => sources.some((source) => source.informationId === item.informationId),
		[sources]
	);
	const handleUpdateCompliance = (fieldUuid, payload, newStatus, currentStatus) => {
		onUpdateCompliance(fieldUuid, payload);
		dispatch(updateCounters({ newStatus, currentStatus }));
		setOpenComplianceDialog(false);
	};
	const complianceItem = useMemo(() => {
		const normalizedStatus = complianceStatus?.toUpperCase();
		const complianceStatusData = criteria?.innerFields?.find((inner) => inner.fieldName === "complianceStatus");
		const isManuallyAdded = complianceStatusData?.value?.isManuallyAdded;
		const userName = complianceStatusData?.value?.userDisplayName;
		if (normalizedStatus === COMPLIANCE_STATUS.COMPLIANT) {
			return {
				icon: "aitCheckCircle",
				text: translate("dashboards.project-sheet.compliance-status.compliant"),
				isManuallyAdded,
				userName,
			};
		}
		if (normalizedStatus === COMPLIANCE_STATUS.NON_COMPLIANT) {
			return {
				icon: "aitTimesCircle",
				text: translate("dashboards.project-sheet.compliance-status.non-compliant"),
				isManuallyAdded,
				userName,
			};
		}
		if (normalizedStatus === COMPLIANCE_STATUS.PENDING) {
			return {
				icon: "aitClock",
				text: translate("dashboards.project-sheet.compliance-status.pending"),
				isManuallyAdded,
				userName,
			};
		}
		return {
			icon: "",
			text: translate("dashboards.project-sheet.compliance-status.not-applicable.description"),
		};
	}, [complianceStatus, criteria]);
	const valueRenderer = ({ value, valueUuid }) => (
		<div className={styles.valueBlock}>
			<div className={styles.valueTextContainer}>
				<div className={styles.valueItemContainer}>
					<div className={styles.valueCompartment}>
						{value && (
							<div className={styles.iconContainer}>
								<IconComponent
									color="var(--blue-500)"
									icon={
										icon[(selectedCriteria?.isManuallyAdded && "faUser") || "faWandMagicSparkles"]
									}
									size="2xs"
								/>
							</div>
						)}
						<div
							ref={containerRef}
							className={`multiple-lines-text ${styles.valueText} ${
								(!value && styles.valueText__empty) || ""
							}`}
							/* eslint-disable-next-line react/no-danger */
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(
									value && value !== "N/A"
										? value
										: translate("dashboards.project-sheet.empty-state.value")
								),
							}}
							style={{
								maxHeight: expanded ? "none" : maxCollapseHeight(LINES),
								lineHeight: `${LINE_HEIGHT}px`,
								WebkitLineClamp: expanded ? "none" : LINES,
							}}
						/>
						<CustomTooltip
							title={
								(!hasRights && translate("common:permissions.insufficient-rights")) ||
								translate("dashboards.project-sheet.section-details.update-value")
							}
						>
							<span>
								<CustomIconButton
									btnClassName={styles.edit__btn}
									disabled={!hasRights}
									icon={icon.faPen}
									size="xs"
									variant="outlined"
									onClick={() => handleOpenUpdateValueDialog({ value, valueUuid })}
								/>
							</span>
						</CustomTooltip>
					</div>
					<div className={styles.seeMoreContainer}>
						{showButton && (
							<CustomButton className={styles.seeMore} onClick={handleShowMore}>
								{translate(`common:btn.see-${expanded ? "less" : "more"}`)}
							</CustomButton>
						)}
					</div>
				</div>
				<div className={styles.valueItemContainer}>
					<div className={styles.valueCompartment}>
						{complianceStatus && complianceStatus !== COMPLIANCE_STATUS.NOT_APPLICABLE && (
							<div className={styles.iconContainer}>
								<CustomTooltip
									title={translate(
										(complianceItem?.isManuallyAdded &&
											"dashboards.project-sheet.compliance-status.user-generated.tooltip") ||
											"dashboards.project-sheet.compliance-status.ai-generated.tooltip",
										{ name: complianceItem?.userName }
									)}
								>
									<IconComponent
										color="var(--blue-500)"
										icon={
											icon[(complianceItem?.isManuallyAdded && "faUser") || "faWandMagicSparkles"]
										}
										size="2xs"
									/>
								</CustomTooltip>
							</div>
						)}
						<div>
							<div className={styles.complianceHeader}>
								<div
									className={styles.complianceStatus}
									data-status={complianceStatus || COMPLIANCE_STATUS.NOT_APPLICABLE}
								>
									<IconComponent icon={icon[complianceItem.icon]} size={16} />
									{complianceItem.text}
								</div>
								<CustomTooltip
									title={
										<span className={styles.formattedTooltip}>
											{criteriaInformation?.complianceRules}
										</span>
									}
								>
									<IconComponent color="var(--grey-500)" icon={icon.faInfoCircle} />
								</CustomTooltip>
							</div>
							{complianceStatus && complianceStatus !== COMPLIANCE_STATUS.NOT_APPLICABLE && (
								<div className={styles.complianceComment}>{complianceComment}</div>
							)}
						</div>
						<CustomTooltip
							title={
								(!hasRights && translate("common:permissions.insufficient-rights")) ||
								translate("dashboards.project-sheet.section-details.compliance.btn.tooltip")
							}
						>
							<span>
								<CustomIconButton
									btnClassName={styles.edit__btn}
									disabled={!hasRights}
									icon={icon.faPen}
									iconColor={!hasRights && "var(--grey-500)"}
									size="xs"
									variant="outlined"
									onClick={handleOpenUpdateComplianceDialog}
								/>
							</span>
						</CustomTooltip>
					</div>
				</div>
				<div className={styles.verifyAction}>
					<CustomTooltip title={validationTooltip}>
						<span>
							<CustomButton
								className={styles.verify__btn}
								disabled={!hasRights}
								size="sm"
								startIcon={checked && icon.faCheckCircle}
								variant={(checked && "outlined") || "contained"}
								onClick={handleCheckValue}
							>
								{translate(
									(checked && "dashboards.project-sheet.section-details.verified-criteria") ||
										"dashboards.project-sheet.section-details.mark-verified-criteria"
								)}
							</CustomButton>
						</span>
					</CustomTooltip>
				</div>
			</div>
		</div>
	);
	return (
		<div className={styles.details} data-is-empty={!selectedCriteria}>
			{!launchInProgress &&
				((!selectedCriteria && (
					<EmptyComponent>
						<span className={styles.emptyComponent}>
							{translate("dashboards.project-sheet.section-details.criteria.empty-space")}
						</span>
					</EmptyComponent>
				)) || (
					<>
						<div className={styles.header}>
							<CustomIconButton
								btnClassName={styles.close__btn}
								icon={icon.faXmark}
								iconClassName={styles.close__btn}
								onClick={onClose}
							/>
							<div className={styles.title}>{selectedCriteria?.name}</div>
							<CustomTooltip title={criteriaInformation?.fieldDescription || "-"}>
								<IconComponent className={styles.info__btn} icon={icon.faInfoCircle} />
							</CustomTooltip>
						</div>
						<div className={styles.valueSection}>
							<div className={styles.valueSection__title}>
								{translate("dashboards.project-sheet.section-details.value-section.title")}
							</div>
							{valueRenderer(content)}
						</div>
						<div className={styles.detailPart}>
							<div data-has-button className={styles.collapse} data-is-open={openDetectedContent}>
								<CustomIconButton
									btnClassName={styles.collapse__button}
									icon={icon[(openDetectedContent && "faChevronDown") || "faChevronRight"]}
									size="sm"
									onClick={handleOpenDetectedContent}
								/>
								<span>
									{translate("dashboards.project-sheet.section-details.detected-content", {
										counter: sources?.length || 0,
									})}
								</span>
								<CustomTooltip
									title={!hasRights && translate("common:permissions.insufficient-rights")}
								>
									<span>
										<CustomButton
											className={styles.associateBtn}
											color="primary"
											disabled={!hasRights}
											startIcon={icon.faPlus}
											variant="outlined"
											onClick={handleOpenAssociateContent}
										>
											{translate("dashboards.project-sheet.section-details.associate-content")}
										</CustomButton>
									</span>
								</CustomTooltip>
							</div>
							{openDetectedContent && (
								<div>
									{isNonEmptyArray(sources) ? (
										sources.map((source, index) => (
											<DetectedContentRow
												// eslint-disable-next-line react/no-array-index-key
												key={`${criteriaInformation?.fieldUuid}-${source?.informationId}-${index}`}
												ref={(el) => (itemRefs.current[source.informationId] = el)}
												source={source}
												onLikeSource={handleLikeSource}
											/>
										))
									) : (
										<div className={styles.detected__emptySpace}>
											{translate("dashboards.project-sheet.empty-state.no-detected-content")}
										</div>
									)}
								</div>
							)}
						</div>
					</>
				))}
			{openAssociateContent && (
				<AssociateContentDialog
					fieldUuid={criteriaInformation?.fieldUuid}
					open={openAssociateContent}
					onCheckSelectedContent={handleCheckResult}
					onClose={handleOpenAssociateContent}
					onSubmit={handleSubmit}
				/>
			)}
			{openUpdateValue && (
				<UpdateValueDialog
					fieldType={criteriaInformation?.fieldType}
					fieldUuid={criteriaInformation?.fieldUuid}
					isMultipleChoice={criteriaInformation?.isMultipleChoice}
					open={openUpdateValue}
					selectedPageId={selectedPageId}
					value={selectedValue}
					onClose={handleOpenUpdateValueDialog}
					onSubmit={handleUpdate}
				/>
			)}
			{openComplianceDialog && (
				<UpdateComplianceDialog
					complianceRules={criteriaInformation?.complianceRules}
					complianceValues={criteria?.innerFields}
					criteriaId={selectedCriteria?.id}
					open={openComplianceDialog}
					onClose={() => setOpenComplianceDialog(false)}
					onSubmit={handleUpdateCompliance}
				/>
			)}
		</div>
	);
};
